import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { v4 as uuidv4 } from 'uuid'
import type { Debt } from '@/types'
import { getRepository } from '@/repository'
import { useLedgerStore } from './ledgerStore'
import { hasPendingWrites, writeEpoch } from './writeTracker'
import { PAID_OFF_TOLERANCE } from '@/engine/tolerances'

interface DebtState {
  debts: Debt[]
  isLoading: boolean
  isInitialized: boolean
  /**
   * v0.8.45 — Captures the message + raw error from a failed
   * `initialize()` so the UI can surface "your cloud data didn't
   * load" instead of silently rendering empty.
   *
   * Pre-fix the catch swallowed the error and left `isInitialized=false`
   * forever — every render saw an empty store with no signal. Audit
   * flagged this and v0.8.32 already established the pattern in
   * `ledgerStore` (`initError` field surfaced by CashFlowView). This
   * brings debtStore in line.
   */
  initError: string | null
}

interface DebtActions {
  initialize: () => Promise<void>
  /**
   * v0.8.45 — Manually re-trigger initialize after a load failure.
   * Clears `initError` and resets `isInitialized` so the next
   * initialize call actually runs (instead of short-circuiting on the
   * "already initialized" guard).
   */
  retryInitialize: () => Promise<void>
  addDebt: (debt: Omit<Debt, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateDebt: (id: string, patch: Partial<Omit<Debt, 'id' | 'createdAt'>>) => Promise<void>
  removeDebt: (id: string) => Promise<void>
  bulkImport: (debts: Debt[]) => Promise<void>
  /**
   * v0.8.44 — Append a batch of new debts without wiping existing ones.
   * Used by the Import dialog's Add-as-new mode. Atomic on cloud (single
   * INSERT statement; either all land or none) and atomic on
   * localStorage (single setItem). Pre-v0.8.44 the Import dialog
   * concatenated existing + new and called bulkImport → bulkReplace,
   * which wiped the workspace first; a 4xx during the insert pass
   * could leave the user with an empty registry.
   */
  bulkAppend: (debts: Debt[]) => Promise<void>
  /**
   * v0.8.41 — Mark a debt as paid off. Sets `balance = 0`, stamps
   * `paidOffAt = now`, and strips the debt's allocations from active
   * planning entries. Closed/skipped ledger entries keep their
   * historical row (same contract as `removeDebt`).
   */
  markPaidOff: (id: string) => Promise<void>
  /**
   * v0.8.41 — Reverse of `markPaidOff`. Clears `paidOffAt` only. Does
   * NOT restore the balance — the user must edit that manually if the
   * debt came back (e.g. paid off in error). Deliberate split so we
   * can't confuse "mistakenly marked" with "rebounded onto a card."
   */
  unmarkPaidOff: (id: string) => Promise<void>
  /**
   * Wipes in-memory state back to its boot defaults so the next render
   * re-initialises from whichever repo is currently bound. Used by
   * `bindReposToSession` when the auth session changes and the backing
   * store flips between localStorage and Supabase.
   */
  /**
   * v0.8.56 — apply a balance the server just reported (atomic payment
   * result). Local state only; the server already holds it.
   */
  applyRemoteBalance: (id: string, balance: number) => void
  /**
   * v0.8.56 — re-read debts in the background (refresh-on-focus) to pick
   * up a household member's changes. No spinner; discarded if a write
   * started meanwhile so an in-flight edit is never overwritten.
   */
  refresh: () => Promise<void>
  reset: () => void
}

export const useDebtStore = create<DebtState & DebtActions>()(
  immer((set, get) => ({
    debts: [],
    isLoading: false,
    isInitialized: false,
    initError: null,

    initialize: async () => {
      if (get().isInitialized) return
      set((s) => {
        s.isLoading = true
        s.initError = null
      })
      try {
        const debts = await getRepository().getAll()
        set((s) => {
          s.debts = debts
          s.isLoading = false
          s.isInitialized = true
          s.initError = null
        })
      } catch (err) {
        // v0.8.45 — surface the failure instead of silently swallowing.
        // The audit caught this: pre-fix a getAll() failure (RLS quirk,
        // schema-cache miss, missing column, network) left
        // isInitialized=false and an empty debts list with no banner,
        // no retry path. Cloud-signed-in users saw an empty Dashboard
        // / Registry and assumed their data was gone — exactly what
        // happened on 2026-06-23 when a workspace-rebind partial state
        // landed the user on cloud repos but reads against a stale
        // RLS context returned PGRST error.
        const message = err instanceof Error ? err.message : String(err)
        console.error('[DebtOS] debtStore.initialize failed:', message)
        set((s) => {
          s.isLoading = false
          s.initError = message
        })
      }
    },

    retryInitialize: async () => {
      set((s) => {
        s.isInitialized = false
        s.initError = null
      })
      await get().initialize()
    },

    addDebt: async (debtData) => {
      const newDebt: Debt = {
        ...debtData,
        id: uuidv4(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      await getRepository().upsert(newDebt)
      set((s) => {
        s.debts.push(newDebt)
      })
      // v0.8.25: auto-append an allocation row for this debt on the active
      // planning entry. Pre-fix, adding a debt via the registry dialog left
      // the Cash Flow page showing the OLD allocation list until the user
      // clicked "Reset from Registry" — which wiped per-month customizations
      // on the others. Lazy `getState()` keeps the static import cycle benign
      // (ledgerStore also imports useDebtStore for the close-month write-back).
      try {
        await useLedgerStore.getState().addAllocationForNewDebt(newDebt)
      } catch (err) {
        // Don't block the debt-add user flow on a ledger sync failure — the
        // debt is persisted; the user can fix planning by clicking
        // "Reset from Registry" if anything went wrong here.
        console.warn(
          `[DebtOS] addAllocationForNewDebt failed for ${newDebt.id}:`,
          err instanceof Error ? err.message : err
        )
      }
    },

    updateDebt: async (id, patch) => {
      const existing = get().debts.find((d) => d.id === id)
      if (!existing) return
      const updated: Debt = { ...existing, ...patch, updatedAt: new Date().toISOString() }
      // v0.8.57 — editing a paid-off debt back to a real balance (the card
      // got used again) un-clears it. Pre-fix the stamp survived: the Registry
      // hid the debt and its balance while the Dashboard, Strategy Engine and
      // Path to Freedom counted it, and Recovery still listed it as cleared.
      if (
        patch.balance !== undefined &&
        patch.balance > PAID_OFF_TOLERANCE &&
        updated.paidOffAt !== undefined
      ) {
        delete updated.paidOffAt
      }
      // v0.8.56 — patch, not upsert: only the changed fields are written, so
      // a household member's concurrent edit to other fields survives.
      await getRepository().patch(existing, updated)
      set((s) => {
        const idx = s.debts.findIndex((d) => d.id === id)
        if (idx >= 0) s.debts[idx] = updated
      })
    },

    removeDebt: async (id) => {
      await getRepository().delete(id)
      set((s) => {
        s.debts = s.debts.filter((d) => d.id !== id)
      })
      // v0.8.25: strip the deleted debt's allocation from every PLANNING
      // ledger entry. Closed / skipped entries keep their historical row
      // (the deleted debt's allocation is part of that month's frozen
      // truth — symmetric with v0.7.13 income orphan cleanup).
      try {
        await useLedgerStore.getState().removeAllocationForDebt(id)
      } catch (err) {
        console.warn(
          `[DebtOS] removeAllocationForDebt failed for ${id}:`,
          err instanceof Error ? err.message : err
        )
      }
    },

    bulkImport: async (debts) => {
      await getRepository().bulkReplace(debts)
      set((s) => {
        s.debts = debts
      })
    },

    bulkAppend: async (newDebts) => {
      if (newDebts.length === 0) return
      await getRepository().appendMany(newDebts)
      set((s) => {
        s.debts.push(...newDebts)
      })
    },

    markPaidOff: async (id) => {
      const existing = get().debts.find((d) => d.id === id)
      if (!existing) return
      const stamp = new Date().toISOString()
      const updated: Debt = {
        ...existing,
        balance: 0,
        paidOffAt: existing.paidOffAt ?? stamp,
        updatedAt: stamp,
      }
      await getRepository().patch(existing, updated)
      set((s) => {
        const idx = s.debts.findIndex((d) => d.id === id)
        if (idx >= 0) s.debts[idx] = updated
      })
      // Strip planning allocations the same way removeDebt does — a
      // paid-off debt shouldn't take a slot in the active planning
      // entry. Closed/skipped entries keep their historical row.
      try {
        await useLedgerStore.getState().removeAllocationForDebt(id)
      } catch (err) {
        console.warn(
          `[DebtOS] removeAllocationForDebt failed for ${id} during markPaidOff:`,
          err instanceof Error ? err.message : err
        )
      }
    },

    unmarkPaidOff: async (id) => {
      const existing = get().debts.find((d) => d.id === id)
      if (!existing || existing.paidOffAt === undefined) return
      // Clone without the field — drop it from the payload so the
      // cloud repo writes paid_off_at = null (rowToDebt reads null as
      // undefined). Balance stays untouched; restoring it is a manual
      // edit by design (see action docstring).
      const next: Debt = { ...existing, updatedAt: new Date().toISOString() }
      delete next.paidOffAt
      // v0.8.56 — patch writes paid_off_at = NULL for the removed field.
      await getRepository().patch(existing, next)
      set((s) => {
        const idx = s.debts.findIndex((d) => d.id === id)
        if (idx >= 0) s.debts[idx] = next
      })
    },

    applyRemoteBalance: (id, balance) => {
      set((s) => {
        const debt = s.debts.find((d) => d.id === id)
        if (debt) debt.balance = balance
      })
    },

    refresh: async () => {
      if (!get().isInitialized || hasPendingWrites()) return
      const startEpoch = writeEpoch()
      try {
        const debts = await getRepository().getAll()
        if (writeEpoch() !== startEpoch || hasPendingWrites()) return
        set((s) => {
          s.debts = debts
        })
      } catch {
        // Background refresh: the next focus retries; the data on screen stays.
      }
    },

    reset: () => {
      set((s) => {
        s.debts = []
        s.isLoading = false
        s.isInitialized = false
      })
    },
  }))
)
